import FuzzySet from "fuzzyset";

import { DesignLayers, ImportStatus } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";

/**
 * Performs fuzzy matching between source items and available layer configurations.
 * Returns the best matches where each configuration is used at most once.
 *
 * @param sourceItems - Array of items to match against (e.g., file names or layer names)
 * @param availableConfigs - Array of layer configurations to match with
 * @param minSimilarity - Minimum similarity threshold (0-1), defaults to 0.5 (50%)
 * @returns Record mapping source items to their best-matched layer configuration
 */
export const fuzzyMatchLayers = (
  sourceItems: string[],
  availableConfigs: LayerConfig[],
  minSimilarity: number = 0.5
): Record<string, LayerConfig> => {
  const fuzzyset = FuzzySet(sourceItems);

  // Find all matches and order by similarity
  const matches: [number, string, string][] = [];
  const configsByLabel: Record<string, LayerConfig> = {};

  for (const config of availableConfigs) {
    const label = config.Label;
    configsByLabel[label] = config;

    // Exclude matches that are less than the minimum similarity threshold
    for (const [similarity, sourceItem] of fuzzyset.get(label, [], minSimilarity)) {
      matches.push([similarity, sourceItem, label]);
    }
  }

  // Sort matches by similarity (highest first)
  matches.sort((prev, next) => next[0] - prev[0]);

  // Get the best match for each source item
  // Each label may only be mapped to one source item
  const suggestions: Record<string, LayerConfig> = {};
  const usedLabels = new Set<string>();

  for (const [, sourceItem, label] of matches) {
    if (!(sourceItem in suggestions) && !usedLabels.has(label)) {
      usedLabels.add(label);
      suggestions[sourceItem] = configsByLabel[label];
    }
  }

  return suggestions;
};

/**
 * Check if the import status should be considered in progress.
 * This includes the following statuses:
 * - PENDING_UPLOAD
 * - BUILDING_TILES
 * - CONVERTING
 * - IMPORTING
 * - STARTING
 *
 * @param status The current import status
 * @returns True if the import is in progress, false otherwise
 */
export const isImportInProgress = (status?: ImportStatus): boolean => {
  if (!status) return false;
  return [ImportStatus.PENDING_UPLOAD, ImportStatus.BUILDING_TILES, ImportStatus.CONVERTING, ImportStatus.IMPORTING, ImportStatus.STARTING].includes(
    status
  );
};

/**
 * Check if any of the specified layer keys have imports in progress.
 *
 * @param inputLayers Map of layer import statuses from useGetImportLayers
 * @param layerKeys Array of layer keys to check for in-progress imports
 * @returns True if any of the specified layers have imports in progress, false otherwise
 *
 * @example
 * const hasInProgressLayers = hasLayerImportInProgress(inputLayers, [
 *   ISGLayerIds.boundary,
 *   ISGLayerIds.address,
 *   ISGLayerIds.parcels
 * ]);
 */
export const hasLayerImportInProgress = (inputLayers: DesignLayers, layerKeys: readonly string[]): boolean => {
  return Array.from(inputLayers.values()).some(
    (layerStatus) =>
      layerStatus.status && isImportInProgress(layerStatus.status) && layerStatus.layer && layerKeys.includes(layerStatus.layer.LayerKey)
  );
};
